// Machine translation for admin-entered product text (name / description), called straight
// from the browser with no API key. Google Translate's public endpoint is tried first; it
// sometimes answers with a bot-check page, so MyMemory is used as a fallback.
const GOOGLE_TRANSLATE_URL = "https://translate.googleapis.com/translate_a/single";
const MYMEMORY_URL = "https://api.mymemory.translated.net/get";
// MyMemory rejects requests with more than 500 characters of text.
const MYMEMORY_MAX_CHARS = 450;

// Google uses the legacy "iw" code for Hebrew.
const GOOGLE_LANG_CODES = { ar: "ar", he: "iw" };

async function translateWithGoogle(text, targetLang) {
  const params = new URLSearchParams({ client: "gtx", sl: "auto", tl: GOOGLE_LANG_CODES[targetLang], dt: "t", q: text });
  const response = await fetch(`${GOOGLE_TRANSLATE_URL}?${params.toString()}`);
  if (!response.ok) throw new Error(`Google translation failed (${response.status})`);

  const data = await response.json();
  // data[0] is a list of [translatedChunk, originalChunk, ...] segments (one per sentence / line).
  const translated = (data?.[0] ?? []).map((segment) => segment?.[0] ?? "").join("");
  if (!translated) throw new Error("Google translation returned no text");
  return translated;
}

// Splits one line into chunks under MyMemory's size limit, preferring sentence breaks.
function chunkLine(line) {
  if (line.length <= MYMEMORY_MAX_CHARS) return [line];
  const chunks = [];
  let current = "";
  line.split(/(?<=[.!?])\s+/).forEach((sentence) => {
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (candidate.length <= MYMEMORY_MAX_CHARS) {
      current = candidate;
      return;
    }
    if (current) chunks.push(current);
    // A single sentence longer than the limit is hard-cut.
    current = "";
    for (let i = 0; i < sentence.length; i += MYMEMORY_MAX_CHARS) {
      const piece = sentence.slice(i, i + MYMEMORY_MAX_CHARS);
      if (i + MYMEMORY_MAX_CHARS < sentence.length) chunks.push(piece);
      else current = piece;
    }
  });
  if (current) chunks.push(current);
  return chunks;
}

async function translateChunkWithMyMemory(chunk, targetLang) {
  const params = new URLSearchParams({ q: chunk, langpair: `en|${targetLang}` });
  const response = await fetch(`${MYMEMORY_URL}?${params.toString()}`);
  if (!response.ok) throw new Error(`MyMemory translation failed (${response.status})`);
  const data = await response.json();
  if (data?.responseStatus !== 200 || !data?.responseData?.translatedText) {
    throw new Error(data?.responseDetails || "MyMemory translation failed");
  }
  return data.responseData.translatedText;
}

async function translateWithMyMemory(text, targetLang) {
  // Translate line by line so line breaks survive; long lines are chunked and re-joined with spaces.
  const lines = await Promise.all(
    text.split("\n").map(async (line) => {
      if (!line.trim()) return line;
      const chunks = await Promise.all(chunkLine(line).map((chunk) => translateChunkWithMyMemory(chunk, targetLang)));
      return chunks.join(" ");
    }),
  );
  return lines.join("\n");
}

// Translates English `text` into `targetLang` ("ar" or "he").
export async function translateText(text, targetLang) {
  const trimmed = (text ?? "").trim();
  if (!trimmed) return "";

  try {
    return await translateWithGoogle(trimmed, targetLang);
  } catch {
    return translateWithMyMemory(trimmed, targetLang);
  }
}

// Translates English `text` into both Arabic and Hebrew in parallel.
export async function translateToArabicAndHebrew(text) {
  const [arabic, hebrew] = await Promise.all([translateText(text, "ar"), translateText(text, "he")]);
  return { arabic, hebrew };
}
